import { apiRequest } from '@/lib/api-client';
import { getStoredAuthToken } from '@/lib/auth-storage';

export type IndicadorVigenteApi = {
  indicador_id: number;
  codigo_interno: string;
  indicador: string;
  categoria: string;
  categoria_tematica?: string | null;
  tipo_indicador?: string | null;
  indicador_version_id: number;
  version: string;
  formula_tipo?: string | null;
  constante?: string | number | null;
  formula_texto?: string | null;
  formula_expression?: string | null;
  unidad_medida: string;
  unidad_simbolo?: string | null;
  periodicidad: string;
  publicable: boolean;
  sensible: boolean;
  vigente_desde?: string | null;
  vigente_hasta?: string | null;
};

export type ResultadoPublicoApi = {
  corrida_id: number;
  jurisdiccion: string;
  periodo_referencia: string;
  codigo_interno: string;
  indicador: string;
  categoria: string;
  unidad_medida: string;
  unidad_simbolo?: string | null;
  valor_resultado: string | number;
  calculado_at?: string;
  publicada_at?: string;
};

export type CorridaPublicadaApi = {
  id: number;
  titulo: string;
  jurisdiccion_id?: number;
  jurisdiccion?: {
    id: number;
    nombre: string;
  } | string | null;
  periodo_referencia?: string | null;
  ejecutada_at?: string | null;
  aprobada_at?: string | null;
  publicada_at?: string | null;
  resultados_publicables_count?: number;
};

export type PaginatedResult<T> = {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

const normalizeList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === 'object') {
    const data = (payload as { data?: unknown[] }).data;
    if (Array.isArray(data)) {
      return data as T[];
    }
  }

  return [];
};

const normalizePaginated = <T,>(payload: unknown, fallbackPerPage = 15): PaginatedResult<T> => {
  const data = normalizeList<T>(payload);
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const p = payload as Record<string, unknown>;
    const meta = (p.meta && typeof p.meta === 'object' ? p.meta : p) as Record<string, unknown>;
    const current_page = Number(meta.current_page ?? p.current_page ?? 1) || 1;
    const last_page = Number(meta.last_page ?? p.last_page ?? 1) || 1;
    const per_page = Number(meta.per_page ?? p.per_page ?? fallbackPerPage) || fallbackPerPage;
    const total = Number(meta.total ?? p.total ?? data.length) || data.length;
    return {
      data,
      current_page,
      last_page,
      per_page,
      total,
    };
  }
  return {
    data,
    current_page: 1,
    last_page: 1,
    per_page: data.length || fallbackPerPage,
    total: data.length,
  };
};

const buildQueryString = (filters?: Record<string, string | number | boolean | undefined>): string => {
  if (!filters) return '';
  const parts = Object.entries(filters)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

const fetchAllPages = async <T,>(
  basePath: string,
  filters?: Record<string, string | number | boolean | undefined>,
  token?: string
): Promise<T[]> => {
  const effectivePerPage = filters?.per_page ?? 200;
  const firstQuery = buildQueryString({ ...filters, per_page: effectivePerPage, page: filters?.page });
  const firstPayload = await apiRequest<unknown>(`${basePath}${firstQuery}`, { token });
  const firstPage = normalizePaginated<T>(firstPayload, Number(effectivePerPage));

  // If a specific page was requested or there is only 1 page, return immediately
  if (filters?.page !== undefined || firstPage.last_page <= 1) {
    return firstPage.data;
  }

  // Otherwise fetch remaining pages to guarantee no records are silently truncated
  const maxPagesToFetch = Math.min(firstPage.last_page, 20);
  const pagePromises: Promise<T[]>[] = [];
  for (let p = 2; p <= maxPagesToFetch; p += 1) {
    const q = buildQueryString({ ...filters, per_page: effectivePerPage, page: p });
    pagePromises.push(
      apiRequest<unknown>(`${basePath}${q}`, { token })
        .then((res) => normalizeList<T>(res))
        .catch(() => [])
    );
  }
  const restPages = await Promise.all(pagePromises);
  return [...firstPage.data, ...restPages.flat()];
};

export type IndicadoresVigentesFilters = {
  codigo_interno?: string;
  categoria?: string;
  categoria_tematica?: string;
  tipo_indicador?: string;
  periodicidad?: string;
  publicable?: boolean;
  sensible?: boolean;
  page?: number;
  per_page?: number;
};

export const fetchIndicadoresVigentes = async (
  filters?: IndicadoresVigentesFilters
): Promise<IndicadorVigenteApi[]> => {
  const token = getStoredAuthToken();
  return fetchAllPages<IndicadorVigenteApi>('/api/externo/indicadores-vigentes', filters, token);
};

export const fetchIndicadoresVigentesPaginated = async (
  filters?: IndicadoresVigentesFilters
): Promise<PaginatedResult<IndicadorVigenteApi>> => {
  const token = getStoredAuthToken();
  const perPage = filters?.per_page ?? 15;
  const queryString = buildQueryString({ ...filters, per_page: perPage });
  const payload = await apiRequest<unknown>(`/api/externo/indicadores-vigentes${queryString}`, { token });
  return normalizePaginated<IndicadorVigenteApi>(payload, perPage);
};

export const fetchResultadosPublicos = async (
  filters?: {
    jurisdiccion?: string;
    codigo_interno?: string;
    indicador?: string;
    categoria?: string;
    periodo_desde?: string;
    periodo_hasta?: string;
    publicada_desde?: string;
    publicada_hasta?: string;
    page?: number;
    per_page?: number;
  }
): Promise<ResultadoPublicoApi[]> => {
  const token = getStoredAuthToken();
  return fetchAllPages<ResultadoPublicoApi>('/api/externo/resultados-publicos', filters, token);
};

export const fetchCorridasPublicadas = async (
  filters?: {
    jurisdiccion_id?: number;
    titulo?: string;
    periodo_desde?: string;
    periodo_hasta?: string;
    publicada_desde?: string;
    publicada_hasta?: string;
    page?: number;
    per_page?: number;
  }
): Promise<CorridaPublicadaApi[]> => {
  const token = getStoredAuthToken();
  return fetchAllPages<CorridaPublicadaApi>('/api/externo/corridas-publicadas', filters, token);
};

export type IndicadorVariableApi = {
  id?: number;
  indicador_version_id?: number;
  dato_fuente_id: number;
  codigo_variable: string;
  rol: string;
  obligatorio?: boolean;
  orden?: number;
  descripcion?: string;
  dato_fuente?: {
    id?: number;
    nombre?: string;
    codigo_interno?: string;
    version?: string | number;
  };
};

export type IndicadorVersionApi = {
  id: number;
  indicador_id?: number;
  tipo_indicador_id?: number;
  unidad_medida_id?: number;
  periodicidad_id?: number;
  version: string;
  formula_tipo?: string;
  constante?: string | number | null;
  formula_texto?: string;
  formula_expression?: string;
  objetivo?: string;
  observaciones_metodologicas?: string;
  vigente_desde?: string;
  vigente_hasta?: string | null;
  activa?: boolean;
  variables?: IndicadorVariableApi[];
};

export type IndicadorDetalleApi = {
  id?: number | string;
  codigo_interno?: string;
  numero?: string;
  nombre?: string;
  indicador?: string;
  categoria_id?: number;
  categoria?: { id?: number; nombre?: string; numero?: number };
  categoria_tematica_id?: number;
  categoria_tematica?: string | { id?: number; nombre?: string };
  tema_id?: number;
  dato_fuente?: { id?: number; nombre?: string; codigo_interno?: string; version?: string | number };
  numerador?: { id?: number; nombre?: string; codigo_interno?: string; version?: string | number };
  denominador?: { id?: number; nombre?: string; codigo_interno?: string; version?: string | number };
  constante?: string | number | null;
  formula_texto?: string;
  formula_tipo?: string;
  formula_expression?: string;
  unidad_medida?: { id?: number; nombre?: string; simbolo?: string };
  unidad_medida_id?: number;
  unidad_simbolo?: string;
  periodo_referencia?: string;
  publicable?: boolean;
  sensible?: boolean;
  activo?: boolean;
  orden?: number;
  periodicidad?: { id?: number; nombre?: string };
  periodicidad_id?: number;
  tipo_indicador_id?: number;
  descripcion?: string;
  observaciones?: string;
  observaciones_metodologicas?: string;
  objetivo?: string;
  vigente_desde?: string;
  vigente_hasta?: string;
  variables?: IndicadorVariableApi[];
  version_activa?: {
    id?: number;
    version?: string;
    tipo_indicador_id?: number;
    unidad_medida_id?: number;
    periodicidad_id?: number;
    constante?: string | number | null;
    formula_texto?: string;
    formula_expression?: string;
    formula_tipo?: string;
    observaciones_metodologicas?: string;
    vigente_desde?: string;
    variables?: IndicadorVariableApi[];
  };
  objetivos_desarrollo_sostenible?: Array<{ id: number; nombre?: string; numero?: number }>;
  ods?: Array<{ ods_id?: number; id?: number }>;
};

export const fetchIndicadoresAdminList = async (filters?: {
  activo?: boolean;
  publicable?: boolean;
  sensible?: boolean;
  categoria_id?: number;
  categoria_tematica_id?: number;
  codigo_interno?: string;
  nombre?: string;
  page?: number;
  per_page?: number;
}): Promise<IndicadorDetalleApi[]> => {
  const token = getStoredAuthToken();
  return fetchAllPages<IndicadorDetalleApi>('/api/indicadores', filters, token);
};

export const fetchIndicadorById = async (id: number | string): Promise<IndicadorDetalleApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorDetalleApi>(`/api/indicadores/${id}`, { token });
};

export const createIndicador = async (data: Record<string, unknown>): Promise<IndicadorDetalleApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorDetalleApi>('/api/indicadores', {
    token,
    method: 'POST',
    body: data,
  });
};

export const updateIndicador = async (id: number | string, data: Record<string, unknown>): Promise<IndicadorDetalleApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorDetalleApi>(`/api/indicadores/${id}`, {
    token,
    method: 'PUT',
    body: data,
  });
};

export const deleteIndicador = async (id: number | string): Promise<void> => {
  const token = getStoredAuthToken();
  await apiRequest<void>(`/api/indicadores/${id}`, {
    token,
    method: 'DELETE',
  });
};

export const fetchIndicadorVersiones = async (
  indicadorId: number | string,
  filters?: { activa?: boolean; per_page?: number }
): Promise<IndicadorVersionApi[]> => {
  const token = getStoredAuthToken();
  return fetchAllPages<IndicadorVersionApi>(`/api/indicadores/${indicadorId}/versiones`, filters, token);
};

export const createIndicadorVersion = async (
  indicadorId: number | string,
  data: Record<string, unknown>
): Promise<IndicadorVersionApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorVersionApi>(`/api/indicadores/${indicadorId}/versiones`, {
    token,
    method: 'POST',
    body: data,
  });
};

export const updateIndicadorVersion = async (
  indicadorId: number | string,
  versionId: number | string,
  data: Record<string, unknown>
): Promise<IndicadorVersionApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorVersionApi>(`/api/indicadores/${indicadorId}/versiones/${versionId}`, {
    token,
    method: 'PUT',
    body: data,
  });
};

export const fetchIndicadorVariables = async (
  indicadorId: number | string,
  versionId: number | string
): Promise<IndicadorVariableApi[]> => {
  const token = getStoredAuthToken();
  return fetchAllPages<IndicadorVariableApi>(
    `/api/indicadores/${indicadorId}/versiones/${versionId}/variables`,
    { per_page: 100 },
    token
  );
};

export const createIndicadorVariable = async (
  indicadorId: number | string,
  versionId: number | string,
  data: {
    dato_fuente_id: number;
    codigo_variable: string;
    rol: string;
    obligatorio?: boolean;
    orden?: number;
    descripcion?: string;
  }
): Promise<IndicadorVariableApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorVariableApi>(
    `/api/indicadores/${indicadorId}/versiones/${versionId}/variables`,
    {
      token,
      method: 'POST',
      body: {
        obligatorio: true,
        ...data,
      },
    }
  );
};

export const updateIndicadorVariable = async (
  indicadorId: number | string,
  versionId: number | string,
  variableId: number | string,
  data: {
    dato_fuente_id: number;
    codigo_variable: string;
    rol: string;
    obligatorio?: boolean;
    orden?: number;
    descripcion?: string;
  }
): Promise<IndicadorVariableApi> => {
  const token = getStoredAuthToken();
  return apiRequest<IndicadorVariableApi>(
    `/api/indicadores/${indicadorId}/versiones/${versionId}/variables/${variableId}`,
    {
      token,
      method: 'PUT',
      body: {
        obligatorio: true,
        ...data,
      },
    }
  );
};

export const deleteIndicadorVariable = async (
  indicadorId: number | string,
  versionId: number | string,
  variableId: number | string
): Promise<void> => {
  const token = getStoredAuthToken();
  await apiRequest<void>(
    `/api/indicadores/${indicadorId}/versiones/${versionId}/variables/${variableId}`,
    {
      token,
      method: 'DELETE',
    }
  );
};

export const syncIndicadorVersionVariables = async (
  indicadorId: number | string,
  versionId: number | string,
  desiredVariables: Array<{
    dato_fuente_id: number;
    codigo_variable: string;
    rol: string;
    obligatorio?: boolean;
    orden?: number;
    descripcion?: string;
  }>
): Promise<void> => {
  const existing = await fetchIndicadorVariables(indicadorId, versionId).catch(() => []);
  const existingByCode = new Map<string, IndicadorVariableApi>();
  for (const item of existing) {
    if (item.codigo_variable) {
      existingByCode.set(item.codigo_variable.toUpperCase(), item);
    }
  }

  const desiredCodes = new Set(desiredVariables.map((v) => v.codigo_variable.toUpperCase()));

  // Delete removed variables
  for (const item of existing) {
    const code = item.codigo_variable?.toUpperCase() || '';
    if (item.id && !desiredCodes.has(code)) {
      await deleteIndicadorVariable(indicadorId, versionId, item.id).catch(() => undefined);
    }
  }

  // Create or update desired variables
  for (let i = 0; i < desiredVariables.length; i += 1) {
    const v = desiredVariables[i];
    const code = v.codigo_variable.toUpperCase();
    const payload = {
      dato_fuente_id: v.dato_fuente_id,
      codigo_variable: code,
      rol: v.rol,
      obligatorio: v.obligatorio ?? true,
      orden: v.orden ?? i + 1,
      descripcion: v.descripcion,
    };
    const found = existingByCode.get(code);
    if (found?.id) {
      await updateIndicadorVariable(indicadorId, versionId, found.id, payload).catch(() => undefined);
    } else {
      await createIndicadorVariable(indicadorId, versionId, payload).catch(() => undefined);
    }
  }
};
